import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { Container, type ContainerProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useBizState } from '@/hooks/use-biz-state';
import { useCustom } from '@/hooks/use-custom';
import { useFlow } from '@/hooks/use-flow';
import { CenteredCard } from '../components/centered-card';
import { Flower } from './flower';

const MyContainer = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
  const { flow } = useFlow();

  useUpdateEffect(() => {
    setTimeout(() => {
      const container = document.getElementById('step-box-container');
      container?.focus();
    }, 200);
  }, [flow]);

  return <Container component="main" ref={ref} {...props} />;
});

const CenteredContainer = styled(MyContainer)<ContainerProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '90%',
  width: '90%',
  padding: 0,
  [theme.breakpoints.up('lg')]: {
    maxWidth: theme.breakpoints.values.xl * 0.85,
  },
  [theme.breakpoints.down('lg')]: {
    maxWidth: theme.breakpoints.values.md * 0.75,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: theme.breakpoints.values.md * 0.75,
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: 'space-between',
    maxWidth: theme.breakpoints.values.sm,
    width: '100%',
  },
}));

const useCenteredRouteHeight = () => {
  const { flow } = useFlow();
  const { layoutConfig } = useCustom();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState<string | undefined>();
  const lastHeightRef = useRef<number>(0);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (!minHeight) {
        const height = Math.ceil(container.getBoundingClientRect().height);
        if (height > 0) {
          lastHeightRef.current = height;
        }
      }
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [minHeight]);

  useLayoutEffect(() => {
    if (layoutConfig?.layout === 'space-between') {
      return;
    }

    if (lastHeightRef.current > 0) {
      setMinHeight(`${lastHeightRef.current}px`);

      const timer = setTimeout(() => {
        setMinHeight(undefined);
      }, 20);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [flow, layoutConfig?.layout]);

  return {
    containerRef,
    minHeight: layoutConfig?.layout === 'space-between' ? undefined : minHeight,
  };
};

export const Centered: React.FC = () => {
  const { socialAuthorizing } = useBizState();
  const { containerRef, minHeight } = useCenteredRouteHeight();

  return (
    <CenteredContainer ref={containerRef} sx={{ minHeight }}>
      <CenteredCard loading={socialAuthorizing}>
        <Flower />
      </CenteredCard>
    </CenteredContainer>
  );
};
